import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@base-app';
import { SectionHeader, DateRangeOptionId } from '@builder';
import { ChartsPanelProps } from './ChartsPanel.types';
import { StyledChartsPanel } from './ChartsPanel.styles';
import { BarChart, BarChartTickXFormats, TooltipVariants, TickY, TickX } from '@charts';
import ChartContainer from '../ChartContainer/ChartContainer';
import { PillIds } from '../types/PillIds';
import { formatConsentedParticipantGraph } from '../../../../utils/participantRecruitmentDataFormatter';
import { Serie } from '@/modules/shared/types/DynamicBarChart.types';
import { InfoTitle } from '@common/components';
import { useLayoutThreeStore } from '@modules/study-insights/store';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import ParticipantDensityMap from '../../Layouts/LayoutThree/ParticipantDensityMap/ParticipantDensityMap';
import { PartnerOrganization } from '@/modules/core/types';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';
import { StyledBaseline, StyledDescription } from '../../shared/ModalParticipants/ModalParticipants.styles';
import { formatNumber } from '@/modules/study-insights/utils';

const ChartsPanel: FC<ChartsPanelProps> = ({
  chartData,
  chartTooltip,
  handleSelectedPill,
  pillOptions,
  selectedPill,
  selectedDateRange,
  showMapSection = false,
  goalLine,
}) => {
  const [formattedData, setFormattedData] = useState<Serie[]>();
  const [dataKey, setDataKey] = useState('value');
  const [tooltipVariant, setTooltipVariant] = useState<TooltipVariants | undefined>(undefined);
  const [tickY, setTickY] = useState<TickY>();
  const { chartMode } = useLayoutThreeStore();
  const [mapTitle, setMapTitle] = useState<string>('');
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [tooltip, setTooltip] = useState<string>('');
  const [tooltipChart, setTooltipChart] = useState<string>('');
  const [consentGoalText, setConsentGoalText] = useState<string>(t('participant_recruitment.weekly_consent_goals'));
  const organizationName = useOrganizationName();
  const isMemorialHermann =
    organizationName === PartnerOrganization.MEMORIAL_HERMANN ||
    organizationName === PartnerOrganization.HELIX_MEMORIAL_HERMANN;

  const tickX: TickX = {
    transformLabelFn: BarChartTickXFormats.label.labelSplitByLinebreak,
    transformLabelCssFn: BarChartTickXFormats.css.cssBoldFirstLine,
  };

  useEffect(() => {
    if (!chartData || !selectedDateRange) {
      return;
    }
    setFormattedData(formatConsentedParticipantGraph(chartData, selectedDateRange));
  }, [chartData, selectedDateRange]);

  useEffect(() => {
    if (!selectedDateRange) {
      return;
    }

    switch (selectedDateRange.selectedDateRange) {
      case DateRangeOptionId.THIS_WEEK:
      case DateRangeOptionId.CUSTOM:
        setConsentGoalText(t('participant_recruitment.daily_consent_goals'));
        break;

      case DateRangeOptionId.THIS_YEAR:
      case DateRangeOptionId.LAST_YEAR:
      case DateRangeOptionId.ALL_TIME:
      case DateRangeOptionId.LAST_QUARTER:
      case DateRangeOptionId.THIS_QUARTER:
        setConsentGoalText(t('participant_recruitment.monthly_consent_goals'));
        break;
      case DateRangeOptionId.THIS_MONTH:
      case DateRangeOptionId.LAST_MONTH:
      default:
        setConsentGoalText(t('participant_recruitment.weekly_consent_goals'));
        break;
    }
  }, [selectedDateRange]);

  const onClickPill = (pillId: string): void => {
    handleSelectedPill?.(pillId);

    switch (pillId) {
      case PillIds.CONSENTED:
        setTooltip(
          t('participant_recruitment.chart_tooltip', { section: pillId.charAt(0).toUpperCase() + pillId.slice(1) }),
        );
        setTickY({
          max: 900,
          min: 0,
          unit: { onlyOnMax: true, symbol: '+' },
        });
        setDataKey('value');
        setTooltipVariant(undefined);
        break;
      case PillIds.INTERESTED:
        setTooltip(
          t('participant_recruitment.chart_tooltip', {
            section: `${
              isMemorialHermann
                ? t('participant_recruitment.available_for_consent.title')
                : pillId.charAt(0).toUpperCase() + pillId.slice(1)
            }`,
          }),
        );
        setTickY({
          max: 900,
          min: 0,
          unit: { onlyOnMax: true, symbol: '+' },
        });
        setDataKey('value');
        setTooltipVariant(undefined);
        break;
      case PillIds.RATE_CONSENTED:
        setTooltip(t('participant_recruitment.consented.tooltip_percentage_of'));
        setTickY({ max: 100, min: 0, unit: { symbol: '%' } });
        setDataKey('convertedPercentage');
        setTooltipVariant(TooltipVariants.CONVERSION);
        break;
      case PillIds.RATE_INTERESTED:
        setTooltip(
          t(
            `participant_recruitment.${
              isMemorialHermann ? 'available_for_consent' : 'interested'
            }.tooltip_percentage_of`,
          ),
        );
        setTickY({
          max: 100,
          min: 0,
          unit: { symbol: '%' },
        });
        setDataKey('convertedPercentage');
        setTooltipVariant(TooltipVariants.CONVERSION);
        break;
      default:
        setTickY(undefined);
        break;
    }
  };

  useEffect(() => {
    setTooltipChart(chartTooltip ?? '');
  }, [chartTooltip]);

  useEffect(() => {
    if (chartMode) {
      chartMode === PillIds.INTERESTED
        ? setMapTitle(t(`participant_recruitment.${isMemorialHermann ? 'available_for_consent' : chartMode}.map_title`))
        : setMapTitle(t(`participant_recruitment.${chartMode}.map_title`));
    }
  }, [chartMode]);

  return (
    <StyledChartsPanel>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '$6' }}>
        <ChartContainer>
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '$4 $7 $8 $7',
              width: '100%',
            }}
          >
            {goalLine && (
              <Box>
                <StyledBaseline />
                <StyledDescription>
                  <Box>{consentGoalText}</Box>
                  <Box css={{ paddingLeft: '$1', fontWeight: '$bold' }}>
                    {t('participant_recruitment.total_participants', { total: formatNumber(goalLine) })}
                  </Box>
                </StyledDescription>
              </Box>
            )}
          </Box>
          {pillOptions && (
            <Box css={{ display: 'none', padding: '$6' }}>
              <SectionHeader handleTogglePills={onClickPill} pillId={selectedPill} pillOptions={pillOptions} />
            </Box>
          )}
          {formattedData && (
            <BarChart
              chartProps={{
                minHeight: 400,
              }}
              data={formattedData}
              dataKey={dataKey}
              tickX={tickX}
              tickY={tickY}
              tooltip={tooltipChart}
              tooltipVariant={tooltipVariant}
              xAxisKey="groupValue"
              baseline={goalLine}
              colors={{ baseline: '#7ACB71' }}
            />
          )}
        </ChartContainer>
        {showMapSection && (
          <ChartContainer css={{ padding: '$0' }}>
            <Box>
              <InfoTitle
                css={{
                  marginBottom: '$0',
                  padding: '$6 $0 $6 $10',
                }}
                testId="density-map-title"
              >
                {mapTitle}
              </InfoTitle>
            </Box>
            <ParticipantDensityMap tooltipText={tooltip} />
          </ChartContainer>
        )}
      </Box>
    </StyledChartsPanel>
  );
};

export default ChartsPanel;
